import { useEffect, useState } from 'react';
import { Download, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PrintButton from '@/components/printing/PrintButton';

/**
 * A stored label, on screen and ready to print.
 *
 * The PDF is fetched as a blob rather than pointed at with an iframe src. Two
 * reasons: the document route needs the session cookie, and holding the blob
 * means Print and Download act on the copy already displayed rather than
 * fetching it again — so what prints is what was checked.
 *
 * `parcelIds` and `mailLabelIds` are for the shop's own printers. The print
 * agent fetches the stored label itself rather than being handed this blob, so
 * a merged PDF or a manifest can be shown here and browser-printed without
 * offering a "send to Zebra" that could not work.
 */
export default function LabelDocumentViewer({
  documentUrl,
  title = 'Label',
  parcelIds = [],
  mailLabelIds = [],
  /** Tailwind height for the frame. The dialog wants tall; a side panel does not. */
  frameClassName = 'min-h-[60vh]',
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!documentUrl) return undefined;

    let cancelled = false;
    let createdUrl = null;

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const response = await fetch(documentUrl, { credentials: 'same-origin' });

        if (!response.ok) {
          // The server explains these properly — a missing archive copy, a
          // carrier that will not hand the label back. Passing its words
          // through beats "Could not load the label (502)".
          const body = await response.json().catch(() => null);
          throw new Error(body?.error ?? `Could not load the label (${response.status})`);
        }

        const blob = await response.blob();
        if (cancelled) return;

        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (fetchError) {
        if (!cancelled) setError(fetchError.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      // Released on unmount; without this every reprint leaks a copy of the PDF
      // into memory for as long as the tab stays open.
      if (createdUrl) URL.revokeObjectURL(createdUrl);
      setBlobUrl(null);
    };
  }, [documentUrl]);

  return (
    <div className="flex flex-col gap-3">
      <div className={`border rounded-lg overflow-hidden bg-slate-100 ${frameClassName}`}>
        {loading && (
          <div className="h-full flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}

        {error && (
          <div className="h-full flex items-center justify-center p-6 text-center">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {blobUrl && !loading && !error && (
          /*
           * `#view=Fit` so the whole label is visible without scrolling. A 4x6
           * page in a panel this size otherwise opens at a zoom that shows the
           * top-left corner and nothing else, which looks like a broken preview
           * rather than a label that needs scrolling.
           *
           * The toolbar stays: zooming in to read a barcode number by eye is a
           * reasonable thing to want.
           */
          <iframe
            src={`${blobUrl}#view=Fit`}
            title={title}
            className={`w-full border-0 ${frameClassName}`}
          />
        )}
      </div>

      <div className="flex gap-2">
        <PrintButton
          blobUrl={blobUrl}
          parcelIds={parcelIds}
          mailLabelIds={mailLabelIds}
          className="flex-1"
        />
        <Button asChild variant="outline" disabled={!blobUrl}>
          <a href={blobUrl ?? '#'} download={`${title}.pdf`}>
            <Download className="w-4 h-4 mr-2" />
            Download
          </a>
        </Button>
        <Button asChild variant="outline" disabled={!blobUrl}>
          <a href={blobUrl ?? '#'} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4 mr-2" />
            Open
          </a>
        </Button>
      </div>
    </div>
  );
}
